import React, { useState } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import TopNavBar from './TopNavBar';
import SideNavBar from './SideNavBar';
import { useAuth } from '../providers/AuthProvider';

const Main = styled('main', {
    shouldForwardProp: (prop) => prop !== 'sidebarExpanded' && prop !== 'isAuthenticated' && prop !== 'isMobile',
})(({ theme, sidebarExpanded, isAuthenticated, isMobile }) => ({
    flexGrow: 1,
    flexShrink: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 'calc(100vh - 64px)', // Subtract AppBar height
    padding: theme.spacing(3),
    marginTop: 64, // Account for AppBar height
    overflow: 'auto',
    // Add left margin based on sidebar state on desktop for authenticated users
    marginLeft: isAuthenticated && !isMobile ? 
        (sidebarExpanded ? 0 : '-10em') : 0, // 0 expanded, -10em collapsed, 0 mobile
    transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    [theme.breakpoints.down('md')]: {
        padding: theme.spacing(1),
        marginLeft: 0, // Always reset margin on mobile
    },
}));

const AppContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexGrow: 1,
    minHeight: 0, // Important for proper flex behavior
    position: 'relative',
}));

/**
 * AppLayout Component
 * 
 * Main layout wrapper that provides:
 * - Top navigation bar for all users
 * - Side navigation for authenticated users
 * - Responsive behavior (mobile drawer vs desktop sidebar)
 * - Proper spacing and layout management
 * 
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children - The main content to render
 */
const AppLayout = ({ children }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { isAuthenticated } = useAuth();
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(true);

    /**
     * Handles mobile drawer toggle
     */
    const handleMobileDrawerToggle = () => {
        setMobileDrawerOpen(!mobileDrawerOpen);
    };

    /**
     * Handles desktop sidebar toggle
     */
    const handleDesktopSidebarToggle = () => {
        setDesktopSidebarOpen(!desktopSidebarOpen);
    };

    return (
        <AppContainer>
            {/* Top Navigation Bar */}
            <TopNavBar 
                onSideNavToggle={handleMobileDrawerToggle}
                sidebarCollapsed={!desktopSidebarOpen}
                isMobile={isMobile}
                isAuthenticated={isAuthenticated}
            />
            
            {/* Content area with sidebar and main content */}
            <ContentWrapper>
                {/* Side Navigation (only for authenticated users) */}
                {isAuthenticated && (
                    <SideNavBar 
                        mobileOpen={mobileDrawerOpen}
                        onMobileToggle={handleMobileDrawerToggle}
                        desktopOpen={desktopSidebarOpen}
                        onDesktopToggle={handleDesktopSidebarToggle}
                    />
                )}
                
                {/* Main Content Area */}
                <Main 
                    id="main-content"
                    role="main"
                    aria-label="Main content"
                    sidebarExpanded={desktopSidebarOpen}
                    isAuthenticated={isAuthenticated}
                    isMobile={isMobile}
                >
                    {children}
                </Main>
            </ContentWrapper>
        </AppContainer>
    );
};

export default AppLayout; 